import { AccessTime } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Container,
  Divider,
  Fade,
  Modal,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import ButtonSolicitacaoRasther from '../layouts/appnavigation/usercheckout/ButtonSolicitacaoRasther';
import HorarioAtendimento from '../layouts/appnavigation/usercheckout/HorarioAtendimento';
import { MotionContainer, varBounceInDown } from './animate';

import { isWorking } from '../services/checkout-service/is-working';
import { sendEmail } from '../services/checkout-service/sendEmail';

// Função de validação do número de série
const isSequential = (str) => {
  const increasing = '0123456789';
  const decreasing = '9876543210';

  return increasing.includes(str) || decreasing.includes(str);
};
const isAllSameDigit = (str) => /^(\d)\1+$/.test(str);
const isValidSerialNumber = (serial) => {
  const onlyDigits = /^\d{6}$/;
  if (!onlyDigits.test(serial) || isAllSameDigit(serial) || isSequential(serial)) {
    return false;
  }
  return true;
};

// Hook e Lógica do Componente
const enableIsWorking = process.env.REACT_APP_ENABLE_IS_WORKING;
function useAtendimentoOnline(pollingInterval = 30000) {
  const [isOnline, setIsOnline] = useState(true);
  const [messageIsWorking, setMessageIsWorking] = useState('');
  const [numSerie, setNumSerie] = useState('');
  const [serverError, setServerError] = useState(false);
  const intervalRef = useRef(null);
  const checkStatus = useCallback(async () => {
    try {
      if (enableIsWorking === 'false') return;
      const { online, message, error } = await isWorking();
      setIsOnline(online);
      setMessageIsWorking(message);
      if (!online) {
        const session = JSON.parse(
          localStorage.getItem('tecnozap_user_session') || '{}'
        );
        setNumSerie(session.numSerie || '');
        setServerError(error);
      } else if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (err) {
      console.error('Erro ao verificar status:', err);
    }
  }, []);
  useEffect(() => {
    checkStatus();
    intervalRef.current = window.setInterval(checkStatus, pollingInterval);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkStatus, pollingInterval]);
  return { serverError, isOnline, messageIsWorking, numSerie, setNumSerie };
}

export default function ForaDoHorarioAtendimento() {
  const theme = useTheme();
  const { serverError, isOnline, messageIsWorking, numSerie, setNumSerie } =
    useAtendimentoOnline();
  const [tituloEmail, setTituloEmail] = useState('');
  const [descriptionEmail, setDescriptionEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [message, setMessage] = useState('');

  // Obter a versão do app da variável de ambiente
  const appVersion = process.env.REACT_APP_VERSION;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!numSerie) {
      setMessage('Por favor, preencha o número de série.');
      return;
    }
    if (!isValidSerialNumber(numSerie)) {
      setMessage(
        'Número de série inválido. Use exatamente 6 números, sem letras, repetições ou sequências.'
      );
      return;
    }
    setEnviando(true);
    try {
      const resp = await sendEmail({
        titulo: tituloEmail,
        descricaoProblema: descriptionEmail,
        numSerie,
      });
      if (resp.status === 200) {
        setMessage('Mensagem enviada com sucesso!');
        setTituloEmail('');
        setDescriptionEmail('');
      }
    } catch (err) {
      console.error('Erro ao enviar:', err);
      setMessage('Erro ao enviar mensagem. Tente novamente mais tarde.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      open={!isOnline}
      closeAfterTransition
      BackdropProps={{
        timeout: 500,
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <Fade in={!isOnline}>
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            overflowY: 'auto',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              textAlign: 'left',
              margin: { xs: '2rem auto', sm: '4rem auto' },
              maxWidth: '550px',
              width: { xs: '90%', sm: '550px' },
              boxShadow: 24,
              backgroundColor: '#fafafa',
              borderRadius: 1,
            }}
          >
            {appVersion && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  position: 'absolute',
                  // ***** VALORES AJUSTADOS AQUI *****
                  top: 16,    // 16px do topo (mais para baixo)
                  right: 24, // 24px da direita (mais para a esquerda)
                  opacity: 0.8,
                }}
              >
                v{appVersion}
              </Typography>
            )}

            <MotionContainer initial="initial" open>
              <motion.div variants={varBounceInDown}>
                <Container
                  maxWidth="sm"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    backgroundColor: 'white',
                    p: 3,
                    borderRadius: 2,
                  }}
                >
                  <HorarioAtendimento isWhiteText={false} />

                  <Stack direction="row" spacing={1} mt={2} justifyContent="center">
                    <AccessTime color="error" />
                    <Typography variant="subtitle1" color="error">
                      {messageIsWorking}
                    </Typography>
                  </Stack>

                  {serverError ? (
                    <Typography variant="body2" sx={{ my: 2 }}>
                      O servidor está temporariamente indisponível. Por favor, tente
                      novamente mais tarde.
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ my: 2 }}>
                      No momento, não há atendentes online. Envie uma mensagem para que possamos responder posteriormente ou aguarde o horário de atendimento.
                    </Typography>
                  )}

                  {message && (
                    <Typography color="primary" sx={{ mb: 2 }}>
                      {message}
                    </Typography>
                  )}

                  {!serverError && (
                    <form onSubmit={handleSubmit}>
                      <TextField
                        label="Número de Série"
                        variant="outlined"
                        fullWidth
                        type="number"
                        value={numSerie}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v.length <= 6) setNumSerie(v);
                        }}
                        sx={{ mb: 2 }}
                        required
                      />
                      <TextField
                        label="Título"
                        variant="outlined"
                        fullWidth
                        value={tituloEmail}
                        onChange={(e) => setTituloEmail(e.target.value)}
                        sx={{ mb: 2 }}
                        required
                      />
                      <TextField
                        label="Descrição do problema"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        value={descriptionEmail}
                        onChange={(e) => setDescriptionEmail(e.target.value)}
                        sx={{ mb: 2 }}
                        required
                      />
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={enviando}
                        sx={{ mb: 1, fontSize: 16, height: 43 }}
                      >
                        {enviando ? 'Enviando...' : 'Enviar'}
                      </LoadingButton>
                      <Divider
                        color={theme.palette.grey[300]}
                        sx={{ width: '100%', my: 1 }}
                      />
                      <ButtonSolicitacaoRasther sx={{ mt: 1 }} />
                    </form>
                  )}
                </Container>
              </motion.div>
            </MotionContainer>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
